import React from 'react';
import {
  ExpandableNavigationItem,
  ExpandableNavigationItemLink,
  ExpandableNavigationItemIcon,
  ExpandableNavigationItemText,
  ExpandableNavigationItemList,
} from 'app/src/components/ExpandableNavigation';
import { HomeTestIds } from '@trello/webdriverio-tests';
import { useTemplateCategoriesQuery } from '@trello/graphql/generated';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { forTemplate } from 'app/common/lib/util/i18n';
import { TemplatesTypeformBanner } from 'app/src/components/Templates/TemplatesTypeformBanner';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { usesEnglish } from '@trello/locale';
import { Analytics, ActionSubjectId } from '@trello/atlassian-analytics';
import { showGalleryLocalization } from 'app/src/components/Templates/Helpers.ts';

const format = forTemplate('templates');
export interface TemplatesTabProps {
  expanded: boolean;
  selected: boolean;
  templateCategory: string;
  href: string;
  onClick: () => void;
  isSubmitPage?: boolean;
}

interface TemplateCategoryTabProps {
  selected: boolean;
  category: string;
  href: string;
  onClick: () => void;
}

export const TemplatesTab: React.FunctionComponent<TemplatesTabProps> = ({
  expanded,
  selected,
  templateCategory,
  isSubmitPage,
  ...props
}) => {
  const {
    data: categoriesQuery,
    error: categoriesError,
  } = useTemplateCategoriesQuery();

  const templateSubmitFeatureFlag = useFeatureFlag(
    'teamplates.template_creation',
    false,
  );
  const templateGalleryLocalizationFeatureFlag = useFeatureFlag(
    'teamplates.gallery-language-support',
    false,
  );

  const categories =
    (categoriesQuery && categoriesQuery.templateCategories) || [];

  const showSubmitATemplate =
    templateSubmitFeatureFlag &&
    (usesEnglish() ||
      showGalleryLocalization(templateGalleryLocalizationFeatureFlag));

  return (
    <ExpandableNavigationItem data-test-id={`${HomeTestIds.Templates}`}>
      <ExpandableNavigationItemLink selected={selected} {...props}>
        <ExpandableNavigationItemIcon iconName={'template-board'} />
        <ExpandableNavigationItemText>
          {format('templates')}
        </ExpandableNavigationItemText>
      </ExpandableNavigationItemLink>
      {expanded && Boolean(categories.length) && (
        <>
          <ExpandableNavigationItemList>
            {categories.map((category) => (
              <TemplateCategoryTab
                category={category.key}
                selected={category.key === templateCategory}
                key={category.key}
                href={`/templates/${category.key}`}
                onClick={() => {
                  trackUe(
                    Noun.HOME,
                    Verb.SHOWS,
                    Noun.TEMPLATE_CATEGORY,
                    '',
                    '',
                    `by clicking ${category.key} in left sidebar`,
                  );
                }}
              />
            ))}
            {showSubmitATemplate && (
              <ExpandableNavigationItem>
                <ExpandableNavigationItemLink
                  indented
                  small
                  selected={isSubmitPage}
                  onClick={() =>
                    Analytics.sendClickedLinkEvent({
                      linkName: ActionSubjectId.submitTemplateLink,
                      source: 'templateGalleryScreen',
                    })
                  }
                  {...{ href: '/templates/submit' }}
                >
                  <ExpandableNavigationItemText>
                    {`+ ${format('submit-a-template')}`}
                  </ExpandableNavigationItemText>
                </ExpandableNavigationItemLink>
              </ExpandableNavigationItem>
            )}
          </ExpandableNavigationItemList>
          {!templateSubmitFeatureFlag && <TemplatesTypeformBanner />}
        </>
      )}
      {categoriesError && <div>{String(categoriesError)}</div>}
    </ExpandableNavigationItem>
  );
};

const TemplateCategoryTab: React.FunctionComponent<TemplateCategoryTabProps> = ({
  category,
  selected,
  ...props
}) => (
  <ExpandableNavigationItem>
    <ExpandableNavigationItemLink
      selected={selected}
      indented
      small
      quiet
      {...props}
    >
      <ExpandableNavigationItemText>
        {format(category).replace(/&amp;/g, '&')}
      </ExpandableNavigationItemText>
    </ExpandableNavigationItemLink>
  </ExpandableNavigationItem>
);
